import flaskApi from "./flaskApi";

const BASE = "/map-policy";

export async function listVendors() {
  const response = await flaskApi.get(`${BASE}/vendors`);
  return response.data?.vendors || [];
}

export async function upsertVendor(payload) {
  const response = await flaskApi.post(`${BASE}/vendors`, payload);
  return response.data?.vendor;
}

export async function uploadPolicy(vendorId, file, { onUploadProgress } = {}) {
  const formData = new FormData();
  formData.append("policy_file", file);
  const response = await flaskApi.post(
    `${BASE}/vendors/${encodeURIComponent(vendorId)}/policies/upload`,
    formData,
    { onUploadProgress }
  );
  return response.data?.policy;
}

export async function listPolicies(vendorId, { includeExpired = true } = {}) {
  const response = await flaskApi.get(
    `${BASE}/vendors/${encodeURIComponent(vendorId)}/policies`,
    { params: { includeExpired: includeExpired ? "true" : "false" } }
  );
  return response.data?.policies || [];
}

export async function getPolicy(policyId) {
  const response = await flaskApi.get(`${BASE}/policies/${policyId}`);
  return response.data?.policy;
}

export async function getParsedStyles(policyId) {
  const response = await flaskApi.get(`${BASE}/policies/${policyId}/styles`);
  return response.data?.parsedStyles;
}

export async function downloadPolicyFile(policyId) {
  const response = await flaskApi.get(`${BASE}/policies/${policyId}/file`, {
    responseType: "blob",
  });
  return {
    data: response.data,
    contentDisposition:
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"] ||
      "",
  };
}

// ---------- Vendor MAP Report --------------------------------------------

export async function listReportVendors() {
  // Returns { vendors: [slug, ...], descriptors: [{ slug, label, files: [...] }, ...] }
  // Descriptors drive the UI's file pickers; the bare list is kept for back-compat.
  const response = await flaskApi.get(`${BASE}/reports/vendors`);
  return {
    vendors: response.data?.vendors || [],
    descriptors: response.data?.vendor_descriptors || [],
  };
}

export async function enqueueVendorMapReport(
  { vendor, shopifyFile, files },
  { onUploadProgress } = {}
) {
  // `files` is a map keyed by the descriptor file `key` (e.g. "map_file",
  // "map_file_2"). Each value is a File object. The server validates which
  // ones are required per vendor.
  const formData = new FormData();
  formData.append("vendor", vendor);
  formData.append("shopify_file", shopifyFile);
  Object.entries(files || {}).forEach(([key, file]) => {
    if (file) formData.append(key, file);
  });
  const response = await flaskApi.post(`${BASE}/reports`, formData, { onUploadProgress });
  return response.data;
}

export async function listVendorMapReportJobs({ page = 1, pageSize = 10 } = {}) {
  // Returns { active, finished, total_finished, page, page_size } — paginated.
  const response = await flaskApi.get(`${BASE}/reports/jobs`, {
    params: { page, page_size: pageSize },
  });
  return response.data || {};
}

export async function getVendorMapReportJob(jobId) {
  const response = await flaskApi.get(`${BASE}/reports/jobs/${encodeURIComponent(jobId)}`);
  return response.data;
}

export async function downloadVendorMapReport(jobId) {
  return flaskApi.get(`${BASE}/reports/jobs/${encodeURIComponent(jobId)}/download`, {
    responseType: "blob",
  });
}

// ---------- MAP Review Queue ---------------------------------------------
// Buyer review of off-MAP (full-priced) products + vendor price drops, by
// vendor. Off-map is synced from live Shopify tags; price drops are ingested
// from a finished Vendor MAP Report. Nothing here changes Shopify prices.

export async function listReviewVendors() {
  // [{ vendorId, vendorDisplayName, total, pending, offMapPending,
  //    offMapFullPricedPending, priceDropPending }]
  const response = await flaskApi.get(`${BASE}/review-items/vendors`);
  return response.data?.vendors || [];
}

export async function listReviewItems({
  vendorId,
  status,
  changeType,
  fullPriced,
  page = 1,
  pageSize = 200,
} = {}) {
  const params = { page, page_size: pageSize };
  if (vendorId) params.vendorId = vendorId;
  if (status) params.status = status;
  if (changeType) params.changeType = changeType;
  if (fullPriced !== undefined && fullPriced !== null) params.fullPriced = fullPriced;
  const response = await flaskApi.get(`${BASE}/review-items`, { params });
  return response.data || { items: [], total: 0, page: 1, page_size: pageSize };
}

export async function setReviewDecision(itemId, { decision, buyerTargetPrice, note } = {}) {
  const payload = { decision };
  if (buyerTargetPrice !== undefined) payload.buyerTargetPrice = buyerTargetPrice;
  if (note !== undefined) payload.note = note;
  const response = await flaskApi.patch(
    `${BASE}/review-items/${encodeURIComponent(itemId)}`,
    payload
  );
  return response.data?.item;
}

export async function ingestReviewItems(jobId) {
  // Pull price-drop rows from a finished Vendor MAP Report job into the queue.
  const response = await flaskApi.post(`${BASE}/review-items/ingest`, { jobId });
  return response.data;
}

export async function enqueueOffmapSync(vendorId) {
  // Per-vendor off-map sync from live Shopify tags (read-only on Shopify).
  const response = await flaskApi.post(`${BASE}/review-items/sync`, { vendorId });
  return response.data;
}

// ---------- MAP List Tracker ---------------------------------------------
// Track the vendor MAP lists the buyer has worked on, by vendor. Dedup is on
// (vendor, SHA-256 of the file), so re-uploading a byte-identical list returns
// a 409 the UI confirms before re-logging. Nothing here touches Shopify.

export async function listTrackedListVendors() {
  // [{ slug, label, shopifyVendor }]
  const response = await flaskApi.get(`${BASE}/tracked-lists/vendors`);
  return response.data?.vendors || [];
}

export async function listTrackedLists({ vendorId, status, page = 1, pageSize = 100 } = {}) {
  const params = { page, page_size: pageSize };
  if (vendorId) params.vendorId = vendorId;
  if (status) params.status = status;
  const response = await flaskApi.get(`${BASE}/tracked-lists`, { params });
  return response.data || { items: [], total: 0, pending: 0, page: 1, page_size: pageSize };
}

export async function createTrackedList(
  { vendor, file, force = false },
  { onUploadProgress } = {}
) {
  // Returns { status, data }. status 201 = logged (data.duplicate tells you if a
  // forced re-log reopened an existing entry); status 409 = conflict — either a
  // byte-identical duplicate (confirm + retry with force=true) or, when
  // data.filenameConflict is true, a different file reusing a tracked name
  // (data.error explains it; the caller must ask the user to rename, NOT force).
  // Other errors throw.
  const formData = new FormData();
  formData.append("vendor", vendor);
  formData.append("map_file", file);
  if (force) formData.append("force", "true");
  try {
    const response = await flaskApi.post(`${BASE}/tracked-lists`, formData, {
      onUploadProgress,
    });
    return { status: response.status, data: response.data };
  } catch (err) {
    if (err.response && err.response.status === 409) {
      return { status: 409, data: err.response.data };
    }
    throw err;
  }
}

export async function setTrackedListStatus(submissionId, { status, note } = {}) {
  const payload = { status };
  if (note !== undefined) payload.note = note;
  const response = await flaskApi.patch(
    `${BASE}/tracked-lists/${encodeURIComponent(submissionId)}`,
    payload
  );
  return response.data?.submission;
}

export async function deleteTrackedList(submissionId) {
  const response = await flaskApi.delete(
    `${BASE}/tracked-lists/${encodeURIComponent(submissionId)}`
  );
  return response.data;
}

export async function downloadTrackedListFile(submissionId) {
  // Streams the original uploaded MAP list back through Flask (the S3 bucket is
  // private). Returns the blob + the Content-Disposition for filename parsing.
  const response = await flaskApi.get(
    `${BASE}/tracked-lists/${encodeURIComponent(submissionId)}/file`,
    { responseType: "blob" }
  );
  return {
    data: response.data,
    contentDisposition:
      response.headers["content-disposition"] ||
      response.headers["Content-Disposition"] ||
      "",
  };
}
